import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import type { UsageLicenseDeniedFeatureFragment } from "@/v2-backoffice-client/usage/denied/__types__/usage.denied.fragment"

/** One feature a scope's people were refused, as the queries return it. */
export type UsageLicenseDeniedFeatureRow = UsageLicenseDeniedFeatureFragment

export type UsageLicenseDeniedWindow = {
  attempts28d: number
  attemptsPrev28d: number
  /** 28 d vs the 28 before; null without a baseline. */
  trendPct: number | null
  /** Features with at least one refused attempt in the window. */
  features28d: number
  /** Products the refusals happened in (app codes), sorted. */
  apps: string[]
  /** Last refused attempt in the read, YYYY-MM-DD; null when none. */
  lastAt: string | null
}

/**
 * Rolls the per-feature rows up into the section's headline numbers. The
 * people count is not here: it comes from the members read (customer) or
 * is one (user), never a sum over features, which would count a person
 * once per feature.
 */
export function deriveLicenseDeniedWindow(
  features: UsageLicenseDeniedFeatureRow[],
): UsageLicenseDeniedWindow {
  const apps = new Set<string>()
  let attempts28d = 0
  let attemptsPrev28d = 0
  let features28d = 0
  let lastAt: string | null = null
  for (const feature of features) {
    attempts28d += feature.Attempts28d
    attemptsPrev28d += feature.AttemptsPrev28d
    if (feature.Attempts28d > 0) {
      features28d += 1
      apps.add(feature.AppCode)
    }
    const last = feature.LastAt ? String(feature.LastAt).slice(0, 10) : null
    if (last && (!lastAt || last > lastAt)) lastAt = last
  }
  return {
    attempts28d,
    attemptsPrev28d,
    trendPct: mcpTrendPct(attempts28d, attemptsPrev28d),
    features28d,
    apps: [...apps].toSorted(),
    lastAt,
  }
}

/** The feature's catalog name, falling back to its code. */
export function formatLicenseDeniedFeature(
  featureCode: string,
  featureName: string | null | undefined,
) {
  return featureName || featureCode
}

/** True when the rows carry anything worth a section: a refusal in 56 days. */
export function hasLicenseDenied(features: UsageLicenseDeniedFeatureRow[]) {
  return features.some(
    (feature) => feature.Attempts28d > 0 || feature.AttemptsPrev28d > 0,
  )
}
